"use strict";
/**
 * Copyright © 2019 Johnson & Johnson
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 * http://www.apache.org/licenses/LICENSE-2.0
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];

      for (var p in s) {
        if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
      }
    }

    return t;
  };

  return __assign.apply(this, arguments);
};

var __rest = this && this.__rest || function (s, e) {
  var t = {};

  for (var p in s) {
    if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  }

  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
};

var __importDefault = this && this.__importDefault || function (mod) {
  return mod && mod.__esModule ? mod : {
    "default": mod
  };
};

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.asEditable = exports.withPlaceholder = void 0;

var react_1 = __importDefault(require("react"));

var react_contenteditable_1 = __importDefault(require("react-contenteditable"));

var mobx_react_lite_1 = require("mobx-react-lite");

var lodash_1 = require("lodash");

var core_1 = require("@bodiless/core");

require("./Editable.css");
/** @type {{search: React.DOMAttributes}} */


var Text = mobx_react_lite_1.observer(function (props) {
  var placeholder = props.placeholder;
  var node = core_1.useNode().node;
  var text = (node.data.text !== undefined ? node.data.text : props.children) || placeholder || ''; // eslint-disable-next-line react/no-danger

  return react_1.default.createElement("span", {
    dangerouslySetInnerHTML: {
      __html: text
    }
  });
});
var EditableText = mobx_react_lite_1.observer(function (props) {
  var node = core_1.useNode().node;

  var onInput = function onInput(event) {
    var newText = event.currentTarget.innerHTML;
    node.setData({
      text: newText
    });
  };

  var pasteAsPlainText = function pasteAsPlainText(event) {
    if (event.clipboardData) {
      event.preventDefault();
      var text_1 = event.clipboardData.getData('text/plain');
      document.execCommand('insertHTML', false, text_1);
    }
  };

  var placeholder = props.placeholder;
  var placeholderDataAttr = placeholder || '';
  var text = (node.data.text !== undefined ? node.data.text : props.children) || '';
  return react_1.default.createElement(react_contenteditable_1.default, {
    tagName: "span",
    className: "bodiless-inline-editable",
    onChange: onInput,
    onPaste: pasteAsPlainText,
    html: text,
    "data-placeholder": placeholderDataAttr
  });
});
var Editable = core_1.withNode(mobx_react_lite_1.observer(function (props) {
  var isEdit = core_1.useEditContext().isEdit;
  return isEdit ? react_1.default.createElement(EditableText, __assign({}, props)) : react_1.default.createElement(Text, __assign({}, props));
}));

var withPlaceholder = function withPlaceholder(placeholder) {
  return function (Component) {
    var WithPlaceholder = placeholder ? function (props) {
      return react_1.default.createElement(Component, __assign({
        placeholder: placeholder
      }, props));
    } : function (props) {
      return react_1.default.createElement(Component, __assign({}, props));
    };
    return WithPlaceholder;
  };
};

exports.withPlaceholder = withPlaceholder;
/**
 * asEditable takes a nodeKey and a placeholder, and returns an HOC which injects
 * an editable span as a child of the wrapped component.  The original children
 * of the wrapped component will become children of the editable span.  In addition,
 * `nodeKey`, `nodeCollection` and `placeholder` props passed to the enhanced
 * component will be forwarded to the editable span.
 *
 * @param nodeKeys A nodeKey or an object containing nodeKey and nodeCollection.
 * @param placeholder A string to use as placeholder text.
 * @return A HOC to inject an editable span.
 */

var asEditable = function asEditable(nodeKeys, placeholder) {
  return function (Component) {
    var EditableChild = lodash_1.flowRight(core_1.withNodeKey(nodeKeys), withPlaceholder(placeholder))(Editable);

    var AsEditable = function AsEditable(props) {
      // @TODO: Improve `withChild` to allow this kind of prop splitting.
      var children = props.children,
          nodeKey = props.nodeKey,
          nodeCollection = props.nodeCollection,
          placeholderProp = props.placeholder,
          rest = __rest(props, ["children", "nodeKey", "nodeCollection", "placeholder"]);

      var editableProps = lodash_1.pickBy({
        children: children,
        nodeKey: nodeKey,
        nodeCollection: nodeCollection,
        placeholder: placeholderProp
      });
      return react_1.default.createElement(Component, __assign({}, rest), react_1.default.createElement(EditableChild, __assign({}, editableProps)));
    };

    return AsEditable;
  };
};

exports.asEditable = asEditable;
exports.default = Editable;